import React from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Package, Clock, FileText, Building2, Truck, ShieldCheck, IndianRupee, Timer, Handshake, ArrowRight, MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { TrackingSection } from "../components/TrackingSection";
import { businessInfo, services, heroStats, whyChooseUs } from "../data/mock";

const iconMap = {
  Package, Clock, FileText, Building2, Truck,
  ShieldCheck, IndianRupee, Timer, Handshake,
};

export default function HomePage() {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-brand-dark overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-brand rounded-full -translate-y-1/2 translate-x-1/3" />
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-brand-light rounded-full translate-y-1/3 -translate-x-1/4" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 mb-6">
                <MapPin className="w-4 h-4 text-brand-light" />
                <span className="text-sm text-white/80">New Delhi's Trusted Courier Service</span>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6" style={{ fontFamily: "'Lora', serif" }}>
                Fast, Reliable &<br />
                <span className="text-brand-light">Affordable Delivery</span><br />
                Across India
              </h1>
              <p className="text-base sm:text-lg text-white/70 leading-relaxed mb-8 max-w-lg">
                Om Sai Express provides safe, timely, and cost-effective courier and logistics solutions. From documents to commercial shipments — we deliver with care.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button asChild size="lg" className="bg-brand hover:bg-brand/90 text-white font-semibold shadow-lg text-base h-12 px-8">
                  <Link to="/book-pickup">
                    <Package className="w-5 h-5" />
                    Book a Pickup
                  </Link>
                </Button>
                <Button asChild size="lg" className="bg-white text-brand-dark hover:bg-gray-50 font-semibold shadow-lg text-base h-12 px-8">
                  <a href={`tel:${businessInfo.phone}`}>
                    <Phone className="w-5 h-5" />
                    Call Now – {businessInfo.phone}
                  </a>
                </Button>
                <Button asChild size="lg" className="bg-[#25D366] hover:bg-[#1da851] text-white font-semibold shadow-lg text-base h-12 px-8">
                  <a href={businessInfo.whatsappLink} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="w-5 h-5" />
                    WhatsApp Us
                  </a>
                </Button>
              </div>
            </div>
            <div className="hidden lg:block">
              <img
                src="https://images.unsplash.com/photo-1616432043562-3671ea2e5242?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxkZWxpdmVyeSUyMHRydWNrfGVufDB8fHx8MTc3MDg3NDk5Mnww&ixlib=rb-4.1.0&q=85&w=700"
                alt="Courier Delivery Truck"
                className="rounded-xl shadow-2xl border-4 border-white/10 w-full h-auto object-cover max-h-[400px]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            {heroStats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-2xl sm:text-3xl font-bold text-brand-dark" style={{ fontFamily: "'Lora', serif" }}>{stat.value}</p>
                <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Track Your Shipment */}
      <TrackingSection />

      {/* Services Overview */}
      <section className="bg-gray-50 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-brand uppercase tracking-wider mb-2">What We Offer</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900" style={{ fontFamily: "'Lora', serif" }}>Our Courier Services</h2>
            <p className="text-gray-500 mt-3 max-w-2xl mx-auto">Comprehensive courier and logistics solutions tailored to meet your delivery needs across India.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 3).map((service) => {
              const IconComp = iconMap[service.icon];
              return (
                <Card key={service.id} className="group hover:shadow-lg transition-shadow border-t-4 border-t-brand bg-white">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-brand-lightest flex items-center justify-center mb-4 group-hover:bg-brand group-hover:text-white transition-colors">
                      {IconComp && <IconComp className="w-6 h-6 text-brand group-hover:text-white transition-colors" />}
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ fontFamily: "'Lora', serif" }}>{service.title}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <div className="text-center mt-10">
            <Button asChild variant="outline" size="lg" className="border-brand text-brand hover:bg-brand hover:text-white font-semibold">
              <Link to="/services">
                View All Services
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-sm font-semibold text-brand uppercase tracking-wider mb-2">Why Om Sai Express</p>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Lora', serif" }}>Trusted by Thousands Across Delhi</h2>
              <p className="text-gray-500 leading-relaxed mb-8">
                With years of experience in the courier industry, Om Sai Express has built a reputation for reliability, affordability, and safe delivery. We partner with India's leading courier companies to ensure the best service for you.
              </p>
              <div className="grid sm:grid-cols-2 gap-6">
                {whyChooseUs.map((item, index) => {
                  const IconComp = iconMap[item.icon];
                  return (
                    <div key={index} className="flex gap-3">
                      <div className="w-10 h-10 rounded-lg bg-brand-lightest flex items-center justify-center shrink-0">
                        {IconComp && <IconComp className="w-5 h-5 text-brand" />}
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-1">{item.title}</h4>
                        <p className="text-xs text-gray-500 leading-relaxed">{item.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1526367790999-0150786686a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwzfHxjb3VyaWVyJTIwZGVsaXZlcnl8ZW58MHx8fHwxNzcwODc0OTg0fDA&ixlib=rb-4.1.0&q=85&w=600"
                alt="Reliable Courier Service"
                className="rounded-xl shadow-lg w-full h-auto object-cover max-h-[400px]"
              />
              <div className="absolute -bottom-4 -left-4 bg-brand text-white px-6 py-3 rounded-lg shadow-lg">
                <p className="text-2xl font-bold" style={{ fontFamily: "'Lora', serif" }}>10+</p>
                <p className="text-xs text-white/80">Years of Service</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-brand py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Lora', serif" }}>Ready to Ship Your Parcel?</h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">Contact us now for a quick pickup or get a quote. We're just a call or WhatsApp message away.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button asChild size="lg" className="bg-white text-brand-dark hover:bg-gray-100 font-semibold shadow-lg text-base h-12 px-8">
              <Link to="/book-pickup">
                <Package className="w-5 h-5" />
                Book a Pickup
              </Link>
            </Button>
            <Button asChild size="lg" className="bg-brand-dark hover:bg-brand-darker text-white font-semibold shadow-lg text-base h-12 px-8 border border-white/20">
              <a href={`tel:${businessInfo.phone}`}>
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </Button>
            <Button asChild size="lg" className="bg-[#25D366] hover:bg-[#1da851] text-white font-semibold shadow-lg text-base h-12 px-8">
              <a href={businessInfo.whatsappLink} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-5 h-5" />
                WhatsApp Us
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
